import { useQueryClient } from '@tanstack/react-query'
import { useState, type Ref } from 'react'
import { Platform, type TextInput, View, type ViewProps } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Textarea } from '@/src/elements/input'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import { getWatchmanDailyIntentionsOptions } from '@/src/features/watchman/services/useWatchmanDailyIntentionsQuery'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import type { TWithBrowserTextareaHeightProps } from '@/src/general/utils/withBrowserTextareaHeight'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TWatchmanDailyIntentionApi } from '../../../api/types/getWatchmanIntentionsApi.types'
import { INTENTION_TYPE } from '../../../models/Intention.enums'
import { useCreateUpliftMutation } from '../../../services/useCreateUpliftMutation'
type TIntentionsPrayFlowThankStepProps = {
  onContinue: VoidFunction
  dailyIntention: TWatchmanDailyIntentionApi
  prayerId: number
} & Pick<ViewProps, 'style'> &
  Partial<TWithBrowserTextareaHeightProps> & { ref?: Ref<TextInput> }

export const IntentionsPrayFlowThankStep = ({
  style,
  dailyIntention,
  prayerId,
  onContinue,
  onLayout,
  onValueUpdate,
  ref,
}: TIntentionsPrayFlowThankStepProps) => {
  const [message, setMessage] = useState('')
  const [initialContentHeight, setInitialContentHeight] = useState<number>(0)
  const { t } = useTranslate()
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useCreateUpliftMutation({ intentionId: dailyIntention.intention.id, prayerId })

  const handleSubmit = async () => {
    if (message) {
      try {
        await mutateAsync(message)
        addToast({ message: t('pray.uplift.sent'), colors: 'success' })
        queryClient.invalidateQueries(getWatchmanDailyIntentionsOptions({ enabled: true }))
      } catch (error) {
        addToast({ message: t(parseApiError(error) || 'uplift.message.error'), colors: 'danger' })
      }
    }
    onContinue()
  }

  const handleChangeText = (text: string) => {
    setMessage(text)
    onValueUpdate?.()
  }

  const userName = dailyIntention.intention.user?.name || ''

  return (
    <View className="grow px-4 web:p-6 gap-6 flex-1 h-full" style={style}>
      <View className="grow w-full justify-center gap-2">
        <GradientTypography classNameWrapper="native:h-20" className="font-black" colors="primary" size="2xl" black>
          {t('watchman.prayers.thanks-for', {
            name: userName,
          })}
        </GradientTypography>
        {dailyIntention.intention.prayersCount > 0 && (
          <Typography colors="primary" contrast="high">
            {t('publication.prayed', { count: dailyIntention.intention.prayersCount })}
          </Typography>
        )}
      </View>
      <View className="w-full gap-4">
        {dailyIntention.intentionType === INTENTION_TYPE.INTENTION && (
          <Textarea
            ref={ref}
            placeholder={t('uplift.textarea.placeholder.long', { userName })}
            numberOfLines={Platform.OS === 'web' ? 2 : undefined}
            color="primary"
            maxLength={400}
            maxHeight={initialContentHeight * 4}
            onContentSizeChange={(e) => {
              const height = e.nativeEvent.contentSize.height
              if (initialContentHeight) return
              setInitialContentHeight(height)
            }}
            onChangeText={handleChangeText}
            onSubmitEditing={handleSubmit}
            onLayout={onLayout}
          />
        )}
        <Button
          testID={message ? TEST_ID.WATCHMAN.INTENTIONS.SEND_BUTTON : TEST_ID.WATCHMAN.INTENTIONS.CONTINUE_BUTTON}
          onPress={handleSubmit}
          isLoading={isPending}
          variant="solid"
          colors="primary"
          startIcon={message ? ICON.SEND : undefined}
          fullWidth
        >
          {message ? t('word.send') : t('word.continue')}
        </Button>
      </View>
    </View>
  )
}
